import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { informesEventoApi, ESTADOS_INFORME } from '../api/informesEvento'
import Boton from '../components/Boton'
import Paginador from '../components/Paginador'
import EstadoBadgeInforme from '../components/EstadoBadgeInforme'
import EstadoChipsFilter from '../components/EstadoChipsFilter'
import { useStoredState } from '../hooks/useStoredState'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { colors, text, radius, sx } from '../design/tokens'

const COLUMNAS = [
  ['Número', 'left'], ['Evento', 'left'], ['Estado', 'left'], ['Facturas', 'right'], ['Lugares', 'right'],
  ['Facturación', 'right'], ['Total gasto', 'right'], ['Balance', 'right'], ['Compensación', 'left'],
]
const CHIPS = Object.entries(ESTADOS_INFORME).map(([value, e]) => ({ value, ...e }))
const VACIOS = { q: '', desde: '', hasta: '', estados: [] }
const td = { padding: '10px 16px' }
const tdNum = { ...td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }

/**
 * Informes de evento (Casa VG): un renglón por evento con su estado en el
 * circuito (abierto → cerrado → completo) y su resultado. Click → el informe.
 */
export default function InformesEventoPage() {
  const navigate = useNavigate()
  const [filtros, setFiltros] = useStoredState('lgr.filtros.informes-evento', VACIOS)
  const [data, setData] = useState({ data: [], current_page: 1, last_page: 1, total: 0 })
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const estados = filtros.estados || []

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const { data: res } = await informesEventoApi.listar({
        page, q: filtros.q || undefined, desde: filtros.desde || undefined, hasta: filtros.hasta || undefined,
        estado: estados.join(',') || undefined,
      })
      setData(res)
    } finally {
      setLoading(false)
    }
  }, [page, filtros]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar() }, [cargar])

  const cambiar = (campo, valor) => { setFiltros({ ...filtros, [campo]: valor }); setPage(1) }
  const hayFiltros = filtros.q || filtros.desde || filtros.hasta || estados.length > 0

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Informes de eventos</h1>
        <Boton variante="primary" onClick={() => navigate('/eventos/informes/nuevo')}>+ Nuevo informe</Boton>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          placeholder="Buscar nº, evento, EVT o factura..."
          value={filtros.q}
          onChange={e => cambiar('q', e.target.value)}
          style={{ ...sx.input, width: 260 }}
        />
        <span style={{ fontSize: text.sec, color: colors.textSec }}>Desde</span>
        <input type="date" value={filtros.desde} onChange={e => cambiar('desde', e.target.value)} style={sx.input} />
        <span style={{ fontSize: text.sec, color: colors.textSec }}>Hasta</span>
        <input type="date" value={filtros.hasta} onChange={e => cambiar('hasta', e.target.value)} style={sx.input} />
        <EstadoChipsFilter
          options={CHIPS}
          selected={estados}
          onToggle={v => cambiar('estados', estados.includes(v) ? estados.filter(x => x !== v) : [...estados, v])}
        />
        {hayFiltros && <Boton variante="ghost" sm onClick={() => { setFiltros(VACIOS); setPage(1) }}>Limpiar</Boton>}
      </div>

      <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.lg, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
          <thead>
            <tr style={{ background: colors.bgSoft }}>
              {COLUMNAS.map(([h, align]) => <th key={h} style={{ ...sx.th, textAlign: align }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading && data.data.length === 0 && (
              <tr><td colSpan={COLUMNAS.length} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>Cargando…</td></tr>
            )}
            {!loading && data.data.length === 0 && (
              <tr><td colSpan={COLUMNAS.length} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>
                {hayFiltros ? 'Ningún informe coincide con el filtro.' : 'Sin informes. Creá el primero con "+ Nuevo informe".'}
              </td></tr>
            )}
            {data.data.map(i => (
              <tr
                key={i.id}
                onClick={() => navigate(`/eventos/informes/${i.id}`)}
                title="Ver el informe"
                style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.borderSoft}` }}
                onMouseEnter={e => e.currentTarget.style.background = colors.bgSoft}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ ...td, ...sx.mono, whiteSpace: 'nowrap' }}>
                  {i.numero}
                  <div style={{ fontSize: text.sec, color: colors.textMeta }}>{fmtFecha(i.fecha)}</div>
                </td>
                <td style={td}>
                  {i.nombre}
                  <div style={{ fontSize: text.sec, color: colors.textMeta }}>
                    <span style={sx.mono}>{i.articulos.map(a => a.codigo_alfa).join(' · ')}</span>
                    {i.tipo_evento ? ` · ${i.tipo_evento}` : ''}
                  </div>
                </td>
                <td style={td}><EstadoBadgeInforme estado={i.estado} /></td>
                <td style={tdNum}>{i.calculos.facturas}</td>
                <td style={tdNum}>{fmtMonto(i.calculos.lugares, 0)}</td>
                <td style={tdNum}>$ {fmtMonto(i.calculos.facturacion)}</td>
                <td style={tdNum}>$ {fmtMonto(i.calculos.total_gasto)}</td>
                <td style={{ ...tdNum, fontWeight: 600, color: i.calculos.balance < 0 ? colors.err : colors.text }}>$ {fmtMonto(i.calculos.balance)}</td>
                <td style={{ ...td, ...sx.mono, whiteSpace: 'nowrap', color: i.compensacion_numero ? colors.text : colors.textMeta }}>{i.compensacion_numero || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginador pagina={data.current_page} totalPaginas={data.last_page} onCambiar={setPage} info={`${data.total} informes`} />
    </div>
  )
}
